<template>
    <div class="bluetooth-gateways-tile">
        <ListHeader>
            <div slot="title">
                <div class="l-inline l-center-v l-gap-2">
                    <GatewayIcon width="24" height="24" />
                    <h3>
                        {{
                            isLoading
                                ? $t('listHeaderPlaceholder')
                                : $tc('listHeader', listData.length)
                        }}
                    </h3>
                </div>
            </div>

            <div
                v-if="listData.length"
                slot="accessory"
                class="l-inline l-gap-2"
            >
                <IconButton
                    v-if="listHeaderState === listHeaderConsts.none"
                    @click="listHeaderState = listHeaderConsts.sort"
                >
                    <SortIcon />
                </IconButton>

                <IconButton
                    v-if="listHeaderState !== listHeaderConsts.none"
                    @click="listHeaderState = listHeaderConsts.none"
                >
                    <RemoveIcon />
                </IconButton>

                <IconButton @click="isHidden = !isHidden">
                    <ArrowDownIcon
                        class="arrow-icon"
                        :class="{ 'arrow-icon--rotated': isHidden }"
                    />
                </IconButton>
            </div>

            <div slot="content">
                <transition name="dropdown">
                    <div
                        v-if="listHeaderState === listHeaderConsts.sort"
                        class="sort-panel l-padded l-stack l-gap-2"
                    >
                        <div>{{ $t('sortedBy') }}</div>

                        <div class="l-stack l-gap-1">
                            <div
                                v-for="(item, index, key) in sortData"
                                :key="key"
                                class="l-inline l-center-v l-gap-2 clickable"
                                @click="sortBy = index + 1"
                            >
                                <input
                                    type="radio"
                                    :checked="sortBy === index + 1"
                                />
                                <div>{{ item.text }}</div>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
        </ListHeader>

        <transition name="dropdown">
            <List
                v-show="!isHidden"
                v-infinite-scroll="loadMoreListData"
                :is-loading="isLoading"
                infinite-scroll-distance="900"
                :class="{ 'u-bb': !isHidden }"
            >
                <ListItem
                    v-for="(item, i) in listDataPortion"
                    :key="i"
                    :clickable="false"
                    class="l-gap-2"
                >
                    <div class="l-grow-1 l-stack">
                        <h3>{{ item.gateway.name }}</h3>

                        <p>
                            <strong>{{ item.gateway.deveui }}</strong>
                        </p>

                        <p class="t-small">
                            {{ formatTimestamp(item.timestamp) }}
                        </p>

                        <TimeAgo
                            class="t-small"
                            :from-datetime="item.timestamp"
                        />
                    </div>

                    <a
                        :href="
                            `#/map/assets/${
                                assetsByAssetId[item.gateway.id].id
                            }`
                        "
                        target="_blank"
                    >
                        <IconButton>
                            <OpenInNewIcon />
                        </IconButton>
                    </a>
                </ListItem>
            </List>
        </transition>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment-timezone'
import infiniteScroll from 'vue-infinite-scroll'

import { httpHelper } from '@/utils'
import ArrowDownIcon from '@/components/icons/ArrowDownIcon'
import GatewayIcon from '@/components/icons/GatewayIcon'
import IconButton from '@/components/IconButton'
import InfinityScrollHelper from '@/mixins/InfinityScrollHelper'
import List from '@/components/List'
import ListHeader from '@/components/ListHeader'
import ListItem from '@/components/ListItem'
import OpenInNewIcon from '@/components/icons/OpenInNewIcon'
import RemoveIcon from '@/components/icons/RemoveIcon'
import SortIcon from '@/components/icons/SortIcon'
import TimeAgo from '@/components/TimeAgo'

const sortConst = {
    LastContactAsc: 1,
    LastContactDesc: 2,
    NameAcs: 3,
    NameDesc: 4,
}
const defaultSort = sortConst.LastContactAsc

export default {
    name: 'BluetoothGatewaysTile',
    components: {
        ArrowDownIcon,
        GatewayIcon,
        IconButton,
        List,
        ListHeader,
        ListItem,
        OpenInNewIcon,
        RemoveIcon,
        SortIcon,
        TimeAgo,
    },
    directives: {
        infiniteScroll,
    },
    mixins: [InfinityScrollHelper],
    props: {
        tracker: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            gateways: [],
            isHidden: true,
            isLoading: false,
            listHeaderConsts: {
                none: 0,
                filter: 1,
                sort: 2,
            },
            listHeaderState: 0,
            sortBy: defaultSort,
            sortData: Object.entries(sortConst).map(([key, value]) => ({
                text: this.$t(key),
                value,
            })),
        }
    },
    computed: {
        ...mapGetters('tracker', ['assetsByAssetId']),
        listData() {
            switch (this.sortBy) {
                case sortConst.LastContactAsc: {
                    return this.gateways.slice().sort((a, b) => {
                        const timeA = a.timestamp
                        const timeB = b.timestamp
                        if (!timeA || timeA < timeB) return 1
                        if (!timeB || timeA > timeB) return -1
                        return 0
                    })
                }
                case sortConst.LastContactDesc: {
                    return this.gateways.slice().sort((a, b) => {
                        const timeA = a.timestamp
                        const timeB = b.timestamp
                        if (!timeA || timeA > timeB) return 1
                        if (!timeB || timeA < timeB) return -1
                        return 0
                    })
                }
                case sortConst.NameAcs: {
                    return this.gateways
                        .slice()
                        .sort((a, b) =>
                            a.gateway.name.localeCompare(
                                b.gateway.name,
                                undefined,
                                { numeric: true }
                            )
                        )
                }
                case sortConst.NameDesc: {
                    return this.gateways
                        .slice()
                        .sort((a, b) =>
                            b.gateway.name.localeCompare(
                                a.gateway.name,
                                undefined,
                                { numeric: true }
                            )
                        )
                }
                default:
                    return this.gateways
            }
        },
    },
    watch: {
        listHeaderState() {
            if (this.listHeaderState !== this.listHeaderConsts.none) {
                this.isHidden = false
            }
        },
        tracker: {
            immediate: true,
            handler() {
                this.isHidden = true
                this.load()
            },
        },
    },
    methods: {
        formatTimestamp(timestamp) {
            return moment(timestamp).format('DD.MM.YYYY HH:mm:ss')
        },
        async load() {
            this.isLoading = true
            const { data } = await httpHelper.get(
                `/ble/asset/${this.tracker.asset}/gws/`
            )
            this.gateways = data || []
            this.isLoading = false
        },
    },
}
</script>

<i18n>
{
    "en": {
        "LastContactAsc": "Last contact (recent → old)",
        "LastContactDesc": "Last contact (old → recent)",
        "NameAcs": "Name (A → Z)",
        "NameDesc": "Name (Z → A)",
        "listHeader": "{n} bluetooth gateway | {n} bluetooth gateways",
        "listHeaderPlaceholder": "Bluetooth gateways",
        "predefinedFormTitle": "Predefined date range",
        "sortedBy": "Sorted by:"
    },
    "de": {
        "LastContactAsc": "Letzter Kontakt (Neu → Alt)",
        "LastContactDesc": "Letzter Kontakt (Alt → Neu)",
        "NameAcs": "Name (A → Z)",
        "NameDesc": "Name (Z → A)",
        "listHeader": "{n} bluetooth gateway | {n} bluetooth gateways",
        "listHeaderPlaceholder": "Bluetooth gateways",
        "predefinedFormTitle": "Vordefinierter Zeitraum",
        "sortedBy": "Sortiert nach:"
    },
    "fr": {
        "LastContactAsc": "Dernier contact (récent → ancien)",
        "LastContactDesc": "Dernier contact (ancien → récent)",
        "NameAcs": "Nom (A → Z)",
        "NameDesc": "Nom (Z → A)",
        "listHeader": "{n} bluetooth gateway | {n} bluetooth gateways",
        "listHeaderPlaceholder": "Bluetooth gateways",
        "predefinedFormTitle": "Plages de dates prédéfinie",
        "sortedBy": "Trié pa:"
    },
    "it": {
        "LastContactAsc": "Ultima Contatto (Recente → Vecchia)",
        "LastContactDesc": "Ultima Contatto (Vecchia → Recente)",
        "NameAcs": "Nome (A → Z)",
        "NameDesc": "Nome (Z → A)",
        "listHeader": "{n} bluetooth gateway | {n} bluetooth gateways",
        "listHeaderPlaceholder": "Bluetooth gateways",
        "predefinedFormTitle": "Intervallo di date predefinito",
        "sortedBy": "Diviso tra:"
    }
}
</i18n>

<style lang="scss">
.bluetooth-gateways-tile {
    .list-header__title {
        font-size: $fs-base;
    }
}
</style>

<style lang="scss" scoped>
.arrow-icon {
    transform: rotate(0.5turn);
    transition: transform 0.2s;

    &--rotated {
        transform: rotate(0);
    }
}

.dropdown {
    &-enter,
    &-leave-to {
        max-height: 0;
        opacity: 0;
    }

    &-leave,
    &-enter-to {
        max-height: 1000px;
        opacity: 1;
    }

    &-enter-active,
    &-leave-active {
        transition: all 0.2s linear;
    }
}

.sort-panel {
    background: $color-gray-lighter;

    .offset {
        margin-left: 20px;
    }

    .split {
        padding: 0.5rem 0;
    }
}
</style>
